import { useState, useEffect, useRef, Fragment } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';
import { AssessmentLayout } from '../components/AssessmentLayout';
import { useIsMobile } from '../hooks/useIsMobile';
import '../styles/assessment-atributo.css';

interface Manifestacao {
    campo: string;
    descricao: string;
    nivel?: number;
}

interface Question {
    campo: string;
    descricao: string;
    pairs: Array<{
        md?: Manifestacao;
        mf?: Manifestacao;
        nivel: number;
    }>;
}

// Embaralhamento determinístico: a mesma semente sempre gera a mesma ordem
function seededShuffle<T>(items: T[], seed: string): T[] {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) {
        h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
    }
    const random = () => {
        h = (h + 0x6D2B79F5) | 0;
        let t = Math.imul(h ^ (h >>> 15), 1 | h);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    const result = [...items];
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
}

export function AssessmentPage() {
    const isMobile = useIsMobile();
    const [questions, setQuestions] = useState<Question[]>([]);
    const [shuffled, setShuffled] = useState<Manifestacao[] | null>(null);
    const [answers, setAnswers] = useState<{ [key: string]: number }>({});
    const [loading, setLoading] = useState(true);
    const [finalizado, setFinalizado] = useState(false);
    const [erroSalvar, setErroSalvar] = useState('');
    const navigate = useNavigate();

    // Preferências de acessibilidade (persistidas)
    const [largeText, setLargeText] = useState(() => localStorage.getItem('a11y_large') === 'true');
    const [highContrast, setHighContrast] = useState(() => localStorage.getItem('a11y_contrast') === 'true');

    // Estado do tema claro/escuro (persistido)
    const [theme, setTheme] = useState<'light' | 'dark'>(() => {
        const saved = localStorage.getItem('capj_theme');
        return saved === 'dark' ? 'dark' : 'light';
    });

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('capj_theme', theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
    };
    const headerRef = useRef<HTMLElement>(null);

    // Mede dinamicamente a altura exata do header para garantir alinhamento milimétrico do sticky
    useEffect(() => {
        const headerEl = headerRef.current;
        if (!headerEl || typeof ResizeObserver === 'undefined') return;

        const updateHeaderHeight = () => {
            const h = headerEl.getBoundingClientRect().height;
            if (h > 0) {
                const roundedH = `${Math.round(h)}px`;
                document.documentElement.style.setProperty('--ao-header-h', roundedH);
                (headerEl.closest('.ao-page') as HTMLElement)?.style.setProperty('--ao-header-h', roundedH);
            }
        };

        const observer = new ResizeObserver(() => updateHeaderHeight());
        observer.observe(headerEl);
        updateHeaderHeight();

        return () => observer.disconnect();
    }, [largeText]);

    const toggleLargeText = () => {
        setLargeText(prev => {
            const next = !prev;
            localStorage.setItem('a11y_large', String(next));
            return next;
        });
    };

    const toggleHighContrast = () => {
        setHighContrast(prev => {
            const next = !prev;
            localStorage.setItem('a11y_contrast', String(next));
            return next;
        });
    };

    const turmaId = sessionStorage.getItem('turma');
    const email = sessionStorage.getItem('email');

    useEffect(() => {
        const token = sessionStorage.getItem('token');
        if (!token) {
            sessionStorage.clear();
            navigate('/login');
            return;
        }

        checkStatus();
        fetchQuestions();
        fetchAnswers();
    }, [navigate]);

    const checkStatus = async () => {
        try {
            const token = sessionStorage.getItem('token');
            if (!token) return;
            const res = await axios.post(`${API.ASSESSMENT}/status`, { token });
            if (res.data?.finalizado === 1) {
                setFinalizado(true);
            }
        } catch (err: any) {
            console.error("Failed to check status", err);
        }
    };

    const handleTurmaFechada = () => {
        alert('Esta turma foi encerrada pelo administrador. Você será redirecionado para o login.');
        sessionStorage.clear();
        navigate('/login');
    };

    const fetchAnswers = async () => {
        try {
            const token = sessionStorage.getItem('token');
            if (!token) return;
            const res = await axios.get(`${API.ASSESSMENT}/answers?token=${token}`);
            const savedAnswers: { [key: string]: number } = {};
            res.data.forEach((item: any) => {
                savedAnswers[item.campo] = item.valor;
            });
            setAnswers(savedAnswers);
        } catch (err: any) {
            if (err.response?.data?.turmaFechada) return handleTurmaFechada();
            console.error("Failed to load answers", err);
        }
    };

    const fetchQuestions = async () => {
        try {
            const res = await axios.get(`${API.ASSESSMENT}/questions?turmaId=${turmaId}`);
            const { caracteristicas, manifestacoes, perguntasEmbaralhadas } = res.data;

            // Agrupa manifestações por característica (pares MD / MF)
            const grouped = caracteristicas.map((c: any) => {
                const ccTag = c.campo.split('_').pop();

                const related = manifestacoes.filter((m: any) => {
                    return m.campo.includes(`_${ccTag}_`);
                });

                // Disfuncionais e funcionais em ordem numérica, pareadas pela posição
                // (1ª com 1ª, 2ª com 2ª...). A quantidade de cada lado pode ser diferente
                // (turma desbalanceada): a que sobra fica sozinha na sua coluna.
                const numero = (m: any) => parseInt(m.campo.match(/(\d+)$/)?.[1] || '0', 10);
                const mds = related.filter((m: any) => /_MD\d+$/.test(m.campo)).sort((a: any, b: any) => numero(a) - numero(b));
                const mfs = related.filter((m: any) => /_MF\d+$/.test(m.campo)).sort((a: any, b: any) => numero(a) - numero(b));
                const pairs = [];
                for (let i = 0; i < Math.max(mds.length, mfs.length); i++) {
                    pairs.push({
                        md: mds[i] ? { ...mds[i], nivel: i + 1 } : undefined,
                        mf: mfs[i] ? { ...mfs[i], nivel: i + 1 } : undefined,
                        nivel: i + 1
                    });
                }

                return { ...c, pairs };
            });

            grouped.sort((a: any, b: any) => {
                const numA = parseInt(a.campo.match(/\d+$/)?.[0] || '0', 10);
                const numB = parseInt(b.campo.match(/\d+$/)?.[0] || '0', 10);
                return numA - numB;
            });

            setQuestions(grouped);

            // Modo embaralhado
            if (perguntasEmbaralhadas) {
                const all: Manifestacao[] = grouped.flatMap((cc: Question) =>
                    cc.pairs.flatMap(pair => [pair.md, pair.mf].filter((m): m is Manifestacao => !!m))
                );
                setShuffled(seededShuffle(all, `${turmaId}|${email}`));
            }

            setLoading(false);
        } catch (err) {
            console.error("Failed to load questions", err);
        }
    };

    const handleAnswer = async (campo: string, valor: number) => {
        if (finalizado) {
            setErroSalvar('Esta avaliação já foi finalizada e submetida. Suas respostas já estão registradas e não podem mais ser alteradas.');
            window.setTimeout(() => setErroSalvar(''), 7000);
            return;
        }

        let anterior = 0;
        setAnswers(prev => { anterior = prev[campo] || 0; return { ...prev, [campo]: valor }; });

        try {
            const token = sessionStorage.getItem('token');
            await axios.post(`${API.ASSESSMENT}/save`, {
                turmaId,
                email,
                token,
                campo,
                valor
            });
        } catch (err: any) {
            if (err.response?.data?.turmaFechada) return handleTurmaFechada();
            console.error("Failed to save answer", err);
            setAnswers(prev => (prev[campo] === valor ? { ...prev, [campo]: anterior } : prev));
            if (err.response?.status === 403) {
                setFinalizado(true);
                setErroSalvar('Esta avaliação já foi finalizada e submetida. Suas respostas já estão gravadas e não podem mais ser alteradas.');
            } else if (err.response?.status === 429) {
                setErroSalvar('Muitas respostas em pouco tempo. Aguarde um instante e marque a resposta de novo.');
            } else {
                setErroSalvar('Não foi possível salvar sua resposta. Verifique sua conexão e marque a resposta de novo.');
            }
            window.setTimeout(() => setErroSalvar(''), 7000);
        }
    };

    const avisoErro = erroSalvar ? (
        <div role="alert" style={{
            position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + var(--ao-header-h, 60px) + 12px)', left: '50%', transform: 'translateX(-50%)',
            zIndex: 100, width: 'min(560px, calc(100% - 32px))', background: '#8a1f2b', color: '#fff',
            padding: '12px 16px', borderRadius: '8px', boxShadow: '0 6px 20px rgba(0,0,0,.35)', fontSize: '14px', lineHeight: 1.4,
            fontWeight: 600
        }}>{erroSalvar}</div>
    ) : null;

    const handleExit = () => {
        if (window.confirm("Deseja realmente sair do assessment? Suas respostas preenchidas estão salvas.")) {
            sessionStorage.clear();
            navigate('/login');
        }
    };

    const handleSubmit = async () => {
        if (finalizado) {
            navigate('/fim');
            return;
        }

        // Checar todas as perguntas cadastradas
        const camposTotal = questions.flatMap(cc => cc.pairs.flatMap(p => [p.md, p.mf].filter((m): m is Manifestacao => !!m).map(m => m.campo)));
        const pendentesCampos = camposTotal
            .map((campo, idx) => ({ campo, num: idx + 1, valor: answers[campo] || 0 }))
            .filter(c => c.valor === 0);

        if (pendentesCampos.length > 0) {
            const numeros = pendentesCampos.map(p => p.num);
            const numerosFormatados = numeros.length <= 15
                ? numeros.join(', ')
                : `${numeros.slice(0, 15).join(', ')}... e mais ${numeros.length - 15}`;

            alert(
                `Não é possível enviar o assessment incompleto.\n\n` +
                `Você ainda precisa responder ${pendentesCampos.length} pergunta(s):\n` +
                `Pergunta(s): ${numerosFormatados}\n\n` +
                `O questionário só pode ser enviado com 100% das respostas.\n` +
                `Por favor, preencha todas as perguntas para submeter.`
            );
            return;
        }

        if (!window.confirm("Deseja realmente finalizar e submeter o assessment agora?\n(Após o envio, suas respostas não poderão mais ser alteradas)")) {
            return;
        }

        try {
            const token = sessionStorage.getItem('token');
            await axios.post(`${API.ASSESSMENT}/submit`, {
                turmaId,
                email,
                token
            });
            navigate('/fim');
        } catch (err: any) {
            if (err.response?.data?.turmaFechada) return handleTurmaFechada();
            if (err.response?.status === 403) {
                setFinalizado(true);
                navigate('/fim');
                return;
            }
            alert(err.response?.data?.error || "Erro ao submeter. Tente novamente.");
        }
    };

    if (loading) return <div className="page-container" style={{ padding: '40px', textAlign: 'center', fontSize: '18px' }}>Carregando assessment...</div>;

    // Em dispositivos móveis (smartphones) OU em modo embaralhado:
    // Utiliza o layout dedicado com uma pergunta por vez, alvos de toque de 58px e navegação simplificada
    if (isMobile || shuffled) {
        const blocos = shuffled
            ? [{ grupos: shuffled.map(m => [m]) }]
            : questions.map((cc, idx) => ({
                titulo: `${idx + 1} - ${cc.descricao.toUpperCase()}`,
                grupos: cc.pairs.map(pair => [pair.md, pair.mf].filter((m): m is Manifestacao => !!m))
            }));

        return (
            <>
                {avisoErro}
                <AssessmentLayout
                    blocos={blocos}
                    answers={answers}
                    onAnswer={handleAnswer}
                    onSubmit={handleSubmit}
                    onExit={handleExit}
                />
            </>
        );
    }

    // Desktop — Proposta 1: O Clássico Refinado (2 Colunas, Banner Superior e Escala Horizontal de 5 Opções)
    const numeracao = new Map<string, number>();
    questions.forEach(cc => cc.pairs.forEach(pair => {
        if (pair.md) numeracao.set(pair.md.campo, numeracao.size + 1);
        if (pair.mf) numeracao.set(pair.mf.campo, numeracao.size + 1);
    }));
    const total = numeracao.size;
    const respondidas = [...numeracao.keys()].filter(campo => (answers[campo] || 0) > 0).length;

    const item = (m: Manifestacao) => (
        <div className="ao-item">
            <span className="ao-num">PERGUNTA {numeracao.get(m.campo)} DE {total}</span>
            <p>{m.descricao}</p>
            <Scale
                selected={answers[m.campo]}
                onChange={(val: number) => handleAnswer(m.campo, val)}
            />
        </div>
    );

    return (
        <div 
            data-theme={theme}
            className={`ao-page${theme === 'dark' ? ' theme-dark' : ''}${largeText ? ' a11y-large' : ''}${highContrast ? ' a11y-contrast' : ''}`}
        >
            {avisoErro}
            <header ref={headerRef} className="ao-header">
                <div className="ao-header-row">
                    <div className="ao-logo-group">
                        <span className="ao-logo">cap jota</span>
                        <span className="ao-sublogo">BMI Assessment CAP Jota</span>
                    </div>

                    {/* Toolbar de Acessibilidade e Tema */}
                    <div className="ao-a11y-tools" role="toolbar" aria-label="Ferramentas de Acessibilidade e Tema">
                        <button 
                            type="button" 
                            className={`ao-a11y-btn${largeText ? ' active' : ''}`}
                            onClick={toggleLargeText}
                            title="Alternar tamanho de texto"
                            aria-pressed={largeText}
                        >
                            <span>A+</span>
                            <span>{largeText ? 'Texto Normal' : 'Texto Maior'}</span>
                        </button>
                        <button 
                            type="button" 
                            className={`ao-a11y-btn${highContrast ? ' active' : ''}`}
                            onClick={toggleHighContrast}
                            title="Alternar modo de alto contraste"
                            aria-pressed={highContrast}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }}>
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" />
                            </svg>
                            <span>Contraste</span>
                        </button>
                        <button 
                            type="button" 
                            className={`ao-a11y-btn${theme === 'dark' ? ' active' : ''}`}
                            onClick={toggleTheme}
                            title={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
                            aria-label={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
                            aria-pressed={theme === 'dark'}
                        >
                            {theme === 'dark' ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }}>
                                    <circle cx="12" cy="12" r="4" />
                                    <path d="M12 2v2" />
                                    <path d="M12 20v2" />
                                    <path d="m4.93 4.93 1.41 1.41" />
                                    <path d="m17.66 17.66 1.41 1.41" />
                                    <path d="M2 12h2" />
                                    <path d="M20 12h2" />
                                    <path d="m6.34 17.66-1.41 1.41" />
                                    <path d="m19.07 4.93-1.41 1.41" />
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }}>
                                    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
                                </svg>
                            )}
                            <span>{theme === 'dark' ? 'Tema Claro' : 'Tema Escuro'}</span>
                        </button>
                    </div>

                    <div className="ao-progress">
                        <div className="ao-progress-top">
                            <span>{respondidas} de {total} respondidas</span>
                        </div>
                        <div className="ao-bar"><i style={{ width: `${total ? (respondidas / total) * 100 : 0}%` }} /></div>
                    </div>
                </div>
            </header>

            {/* Banner Central do Legado Fixo */}
            <div className="ao-title-sticky">
                <div className="ao-title-inner">
                    <h1>BMI ASSESSMENT CAP JOTA</h1>
                </div>
            </div>

            <div className="ao-main">
                {questions.map((cc, idx) => (
                    <section key={idx} className="ao-block">
                        <div className="ao-block-header-sticky">
                            <h2 className="ao-block-title">{idx + 1} - {cc.descricao.toUpperCase()}</h2>
                        </div>

                        {cc.pairs.map((pair, pIdx) => (
                            <div key={pIdx} className="ao-pair">
                                {pair.md ? item(pair.md) : <div className="ao-item" aria-hidden="true" />}
                                {pair.mf ? item(pair.mf) : <div className="ao-item" aria-hidden="true" />}
                            </div>
                        ))}
                    </section>
                ))}

                <div className="ao-buttons">
                    <button type="button" className="ao-btn ao-btn-submit" onClick={handleSubmit}>Submeter Avaliação</button>
                    <button type="button" className="ao-btn ao-btn-exit" onClick={handleExit}>Sair</button>
                </div>
            </div>
        </div>
    );
}

// Escala de 5 opções compactas em linha horizontal (Proposta 1 — O Clássico Refinado)
function Scale({ selected, onChange }: { selected: number; onChange: (val: number) => void }) {
    const options = [
        { val: 1, label: "NÃO OBSERVADA" },
        { val: 2, label: "RARAMENTE OBSERVADA" },
        { val: 3, label: "COM CERTA FREQUÊNCIA" },
        { val: 4, label: "MARCANTE" },
        { val: 5, label: "FORTEMENTE INCORPORADA" }
    ];

    return (
        <div className="ao-scale" role="group" aria-label="Escala de avaliação">
            {options.map((opt) => {
                const isSelected = selected === opt.val;
                return (
                    <button
                        key={opt.val}
                        type="button"
                        aria-pressed={isSelected}
                        className={`ao-opt${isSelected ? ' ao-opt-selected' : ''}`}
                        onClick={() => onChange(isSelected ? 0 : opt.val)}
                        title={opt.label}
                    >
                        <span className="ao-opt-label">
                            {opt.label}
                            {isSelected && <span className="ao-opt-check" aria-hidden="true">✓</span>}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
